import { useEffect, useState, type VoidFunctionComponent } from 'react';
import type { GetOptionLabel } from 'react-select';

import { compactRevision } from '../../../helpers/revisions';
import useProjectBranches from '../../../hooks/api/projects/useProjectBranches';
import useProjectRevisions from '../../../hooks/api/revisions/useProjectRevisions';
import useDebouncedInputValue from '../../../hooks/useDebouncedInputValue';
import { selectOptionFactory } from '../../../utils';
import Select from '../Select/Select';
import type { LatestRevision } from '../../../../shared/projectsV2/common';
import type { GetProjectRevisionsParams } from '../../../hooks/api/revisions/useProjectRevisions';
import type { AddedProject, MinimalRevision } from '../../../shared/branches/types';
import type { SelectOption } from '../../../types';

import styles from './BranchRevisionCard.module.less';

type RevisionOption = SelectOption & {
  revision: MinimalRevision;
  latestRevision?: LatestRevision;
};

//  Individual option for the revision dropdown
const ProjectReleaseRevisionSelectOption = (({
  value,
  label,
  revision,
  latestRevision,
}: RevisionOption) => {
  const revisionTag = compactRevision(revision.locator);
  return (
    <div className={styles.revisionRow}>
      <div className={styles.revisionHashAndLabel}>
        {revisionTag && <span className={styles.hashBadge}>{revisionTag}</span>}
        <div className={styles.revisionLabel}>{label}</div>
      </div>
      {value === latestRevision?.locator && <div className={styles.latestBadge}>latest</div>}
    </div>
  );
}) as unknown as GetOptionLabel<RevisionOption>;

const revisionToOption = (revision: MinimalRevision, latestRevision?: LatestRevision): RevisionOption => {
  return {
    ...selectOptionFactory(
      revision.locator,
      revision.message ?? revision.locator,
    ),
    //  Include the current and latest revisions updating the addedProject when changed
    revision,
    latestRevision,
  };
};

export type Props = {
  addedProject: AddedProject;
  onUpdateAddedProject: (project: AddedProject) => void;
};

/**
 * A generic representation of a card that allows the user to select a branch and revision for a project.
 */
const BranchRevisionCard: VoidFunctionComponent<Props> = ({ addedProject, onUpdateAddedProject }) => {
  const [isBranchMenuOpen, setIsBranchMenuOpen] = useState<boolean>(false);
  //  When isBranchMenuOpen is false, this will not fetch branches.
  const { data: branches, isFetching: areBranchesLoading } = useProjectBranches(addedProject.locator, isBranchMenuOpen);

  // Keep track of the revision dropdown's search input.
  const {
    value: search,
    onChange: onSearchRevisions,
    debouncedValue: debouncedRevisionSearch,
  } = useDebouncedInputValue('');

  const fetchProjectRevisionParams: GetProjectRevisionsParams = {
    projectLocator: addedProject.locator,
    revisionLocatorFilter: debouncedRevisionSearch,
    count: 1000,
    refsType: 'branch',
    refName: addedProject.branch,
  };
  const {
    data: revisionBranchMap,
    isLoading: areRevisionsLoading,
  //  Only fetch revisions once there has been a branch selected
  } = useProjectRevisions(fetchProjectRevisionParams, !!addedProject.branch);

  const branchOptions: SelectOption[] = branches?.map((branch) => selectOptionFactory(branch.name)) ?? [];
  const revisions = revisionBranchMap?.branch?.[addedProject.branch] ?? [];
  const revisionOptions = revisions.map((revision) => revisionToOption(revision, addedProject.latestRevision));

  const onChangeBranch = (branchOption: SelectOption | null) => {
    if (!branchOption) {
      return;
    }

    // Update the added project's selected branch and unset its revision.
    // Unsetting the revision will cause a new one to be auto-selected when the revisions for this branch load in.
    onUpdateAddedProject({ ...addedProject, branch: branchOption.value, revision: undefined });
  };

  const onChangeRevision = (revisionOption: RevisionOption | null) => {
    if (!revisionOption) {
      return;
    }

    // Update the added project's selected revision.
    onUpdateAddedProject({ ...addedProject, revision: revisionOption.revision });
  };

  //  This auto-selects the first revision whenever the revisions load in.
  //  This works because we only re-fetch revisions when the branch changes.
  useEffect(() => {
    if (revisions.length > 0 && !addedProject.revision) {
      const [revision] = revisions;
      onUpdateAddedProject({ ...addedProject, revision, latestRevision: revision });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- We ONLY want to run this effect when the revisions change.
  }, [revisions]);

  return (
    <div className={styles.card}>
      <div className={styles.branchInput}>
        <span>Branch</span>
        <Select
          id="BranchDropdown"
          onMenuOpen={() => setIsBranchMenuOpen(true)}
          onMenuClose={() => setIsBranchMenuOpen(false)}
          isLoading={areBranchesLoading}
          value={selectOptionFactory(addedProject.branch)}
          onChange={onChangeBranch}
          options={branchOptions}
        />
      </div>
      <div className={styles.revisionInput}>
        <span>Revision</span>
        <Select
          id="RevisionDropdown"
          isLoading={areRevisionsLoading}
          value={addedProject.revision
            ? revisionToOption(addedProject.revision, addedProject.latestRevision)
            : null}
          onChange={onChangeRevision}
          options={revisionOptions}
          getOptionLabel={ProjectReleaseRevisionSelectOption}
          inputValue={search}
          onInputChange={(value: string) => onSearchRevisions(value)}
        />
      </div>
    </div>
  );
};

export default BranchRevisionCard;
